import {
    Drawer, Box, Typography, Stack, TextField, MenuItem, Button
} from "@mui/material";
import { ShipmentEntity } from "../dto/shipment/ShipmentEntity";
import { useEffect, useState } from "react";
import { UserAddressEntity } from "../dto/User/UserAddressEntity";
import toast, { Toaster } from 'react-hot-toast';
import { shipmentUpdate } from "../controller/ShipmentController";
import { getUserAddresses } from "../controller/UserController";

interface Props {
    open: boolean;
    shipment: ShipmentEntity | null;
    onClose: () => void;
}

const STATUS_OPTIONS = [ "CREATED", "AWAITING_SHIPPING_PAYMENT", "SHIPPED", "READY_FOR_PICKUP", "ASSIGNED_TO_DRIVER", "PICKED_UP", 
    "DELIVERED", "DELIVERY_CONFIRMED", "FAILED", "CANCELLED", "AWAITING_EXTRA_SHIPPING_PAYMENT" ];

export const ShipmentEditDrawer = ({ open, shipment, onClose }: Props) => {

    const [pickupAddresses, setPickupAddresses] = useState<UserAddressEntity[]>([]);
    const [deliveryAddresses, setDeliveryAddresses] = useState<UserAddressEntity[]>([]);
    const [loadingAddresses, setLoadingAddresses] = useState(false);
    const [form, setForm] = useState<ShipmentEntity | null>(null);

    useEffect(() => {
        if (shipment) {
            setForm(shipment);
        }
    }, [shipment]);

    useEffect(() => {
        if (!shipment || !open) return;

        const loadAddresses = async () => {
            setLoadingAddresses(true);
            try {                                                             
                // const [pickupRes, deliveryRes] = await 
                const sellerAddresses = await getUserAddresses(shipment.sellerId!);
                const buyerAddresses = await getUserAddresses(shipment.buyerId!);
                console.log(sellerAddresses);
                console.log(buyerAddresses);
                
                setPickupAddresses(sellerAddresses.data.beans);
                setDeliveryAddresses(buyerAddresses.data.beans);
            } finally {
                setLoadingAddresses(false);
            }
        }
        
        loadAddresses();
    }, [shipment, open]);

    const formSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        try {
            const response = await shipmentUpdate(form!);
            
            if (response.status === 0) {
                toast.success(response.msg);
            } else {
                toast.error(response.msg);
            }
        } catch(e: any) {
            toast.error("Амжилтгүй");
        }
      }
    
    if (!shipment) return null;
    return (
        <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: 420 } }}
        >
            <Box p={3} height="100%" display="flex" flexDirection="column">
                {/* Header */}
                <Typography variant="h6">
                Product #{shipment.orderId}
                </Typography>
                <Typography variant="caption" color="text.secondary" mb={2}>
                Shipment: #{shipment.id}
                </Typography>
        
                <Stack spacing={3} flex={1} overflow="auto">
                    {/* Status */}
                    <TextField
                        select
                        label="Status"
                        value={form?.status ?? ""}
                        onChange={(e) => 
                            setForm((prev) => 
                                prev
                                ? {...prev, status: e.target.value}
                                : prev
                            )
                        }
                        fullWidth>
                        {STATUS_OPTIONS.map((status) => (
                        <MenuItem key={status} value={status}>
                            {status}
                        </MenuItem>
                        ))}
                    </TextField>
            
                    {/* Pickup Address */}
                    <TextField
                    select 
                    fullWidth
                    label="Pickup Address"
                    value={form?.pickupAddressId ?? ""}
                    onChange={(e) => 
                        setForm((prev) => 
                            prev
                            ? {...prev, pickupAddressId: Number(e.target.value)}
                            : prev
                        )
                    }
                    disabled={loadingAddresses}>
                        {pickupAddresses.length === 0 ? (
                            <MenuItem disabled>No saved addresses</MenuItem>
                        ) : (
                            pickupAddresses.map((addr) => (
                                <MenuItem key={addr.id} value={addr.id}>
                                    {[addr.city, addr.district, addr.avenue, addr.description]
                                    .filter(Boolean)
                                    .join(", ")}
                                </MenuItem>
                            ))
                        )}
                    </TextField>
                    {/* Delivery Address */}
                    <TextField
                    select 
                    fullWidth
                    label="Delivery Address"
                    value={form?.deliveryAddressId ?? ""}
                    onChange={(e) => 
                        setForm((prev) => 
                            prev
                            ? {...prev, deliveryAddressId: Number(e.target.value)}
                            : prev
                        )
                    }
                    disabled={loadingAddresses}>
                        {deliveryAddresses.length === 0 ? (
                            <MenuItem disabled>No saved addresses</MenuItem>
                        ) : (
                            deliveryAddresses.map((addr) => (
                                <MenuItem key={addr.id} value={addr.id}>
                                    {[addr.city, addr.district, addr.avenue, addr.description]
                                    .filter(Boolean)
                                    .join(", ")}
                                </MenuItem>
                            ))
                        )}
                    </TextField>
                    <TextField
                    fullWidth
                    label="Weight grams"
                    type="number"
                    value={form?.weightGrams ?? ""}
                    onChange={(e) =>
                        setForm((prev) =>
                            prev
                                ? { ...prev, weightGrams: Number(e.target.value)}
                                : prev
                        )
                    } />
                    <TextField
                    fullWidth
                    label="Driver Phone"
                    type="text"
                    value={form?.driverPhoneNo ?? ""}
                    onChange={(e) =>
                        setForm((prev) =>
                            prev
                                ? { ...prev, driverPhoneNo: e.target.value}
                                : prev
                        )
                    } />
                    <TextField
                    fullWidth
                    label="Driver Name"
                    type="text"
                    value={form?.driverName ?? ""}
                    onChange={(e) =>
                        setForm((prev) =>
                            prev
                                ? { ...prev, driverName: e.target.value}
                                : prev
                        )
                    } />
                    <TextField
                    fullWidth
                    label="Vehicle No"
                    type="text"
                    value={form?.vehicleNo ?? ""}
                    onChange={(e) =>
                        setForm((prev) =>
                            prev
                                ? { ...prev, vehicleNo: e.target.value}
                                : prev
                        )
                    } />
                    <TextField
                    fullWidth
                    label="Reason"
                    type="text"
                    value={form?.reason ?? ""}
                    onChange={(e) =>
                        setForm((prev) =>
                            prev
                                ? { ...prev, reason: e.target.value}
                                : prev
                        )
                    } />
                </Stack>
        
                {/* Footer */}
                <Stack direction="row" spacing={2} mt={3}>
                    <Button fullWidth variant="outlined" onClick={onClose}>
                        Close
                    </Button>
                    <Button fullWidth variant="contained" onClick={formSubmit}>
                        Save
                    </Button>
                </Stack>
            </Box>
        </Drawer>
    );
};